/**
 * <product-assurances>
 *
 * Keeps the free-shipping tile of the assurances box in step with the cart. The
 * block renders both halves of the sentence — the rule ("On orders over €69")
 * and the earned state — with the server's answer showing; this element switches
 * between them whenever the cart changes, so adding the third print flips the
 * tile without a reload, and emptying the cart flips it back.
 *
 * The total comes from the cart JSON rather than the update event, which carries
 * the item count but not the money, and the threshold is a money question.
 *
 * Only this one tile is live. The rest are static copy, and the frame-dependent
 * tile is handled in CSS — see blocks/_product-assurances.liquid.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

class ProductAssurances extends HTMLElement {
  connectedCallback() {
    const tile = this.querySelector('[data-free-shipping]');
    this.ruleElement = this.querySelector('[data-free-shipping-state="rule"]');
    this.earnedElement = this.querySelector('[data-free-shipping-state="earned"]');
    this.threshold = Number(tile?.dataset.threshold ?? 0);

    // Nothing to switch between: this market has no threshold, or one of the two
    // texts was cleared in the theme editor.
    if (!this.threshold || !this.ruleElement || !this.earnedElement) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    // Restored from the back/forward cache — the cart can have moved on since
    // this page was rendered.
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    // The event is dispatched as the request goes out, and its promise settles
    // once the cart has actually changed. Reading the cart on the event itself
    // reads the previous one — which showed up as the tile lagging one add
    // behind: three prints in, it still quoted the rule.
    const pending = event?.promise;

    if (!pending) {
      this.#refresh();
      return;
    }

    pending
      .then(() => this.#refresh())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-assurances] Cart update rejected:', error);
      });
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  async #refresh() {
    let cart;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

      cart = await response.json();
    } catch (error) {
      // Leave the server's answer standing rather than guessing at a claim about
      // what the shopper will pay.
      console.warn('[product-assurances] Could not read the cart:', error);
      return;
    }

    // The same two tests the block makes server-side: a cart of gift cards alone
    // earns nothing, because nothing in it ships.
    const shippable = (cart.items ?? []).some((item) => item.requires_shipping);
    const earned = shippable && cart.total_price >= this.threshold;

    this.ruleElement.hidden = earned;
    this.earnedElement.hidden = !earned;
  }
}

if (!customElements.get('product-assurances')) {
  customElements.define('product-assurances', ProductAssurances);
}
